/**
 * Chat da IA em painel LATERAL (02/09/2026 — pedido do dono: tirar o chat de
 * dentro do accordion). Botão FLUTUANTE (Affix no canto inferior direito, com
 * Badge quando há mensagens) que abre um Drawer à direita com a conversa
 * completa da competência aberta: histórico, bolhas usuário × IA, Enter envia
 * (Shift+Enter quebra linha).
 *
 * Redesenho 04/09/2026 (pedido do dono — "o input quase some na parte de
 * baixo" + chat agêntico):
 *  - LAYOUT FLEX sem calc mágico: o corpo do Drawer (600px) é uma coluna flex
 *    de altura total — topo fixo (ajuda colapsável, FECHADA por padrão), meio
 *    = lista com scroll próprio (flex: 1 / minHeight: 0), rodapé = composer
 *    fixo. Auto-scroll ao fim da lista ao abrir e a cada mensagem nova.
 *  - ANEXOS: botão de clipe (FileButton múltiplo) e Ctrl+V no campo (print de
 *    tela cola como imagem, nomeada print-AAAAMMDD-HHmmss.<ext>). Upload
 *    ANTECIPADO (POST .../ia/anexos assim que anexado; chip com spinner) e o
 *    POST chat leva só os ids (`anexoIds`). Máx. 5 por mensagem.
 *  - MODO "Investigação profunda" (Switch): vai `modo: "profundo"` no POST e o
 *    texto de espera avisa que pode levar alguns minutos.
 *  - TRILHA do assistente (mensagem.meta): ferramentas consultadas (linha
 *    discreta + detalhes), subagentes (modo profundo) e PROPOSTAS de
 *    justificativa — card com botão "Aplicar justificativa": a IA propõe, o
 *    clique humano grava via onAplicarJustificativa (POST /divergencias/:id/
 *    justificar da página). meta ausente (mensagens antigas) = parse tolerante.
 *
 * Bloco de AÇÃO (```acao): quando a resposta do assistente termina com um
 * fence ```acao contendo UMA ação JSON do schema divergencias-correcoes@v1,
 * o bloco é ESCONDIDO do texto e vira um card com o botão "Preparar
 * correção" — o MESMO fluxo de import com dry-run/prévia do passo 7 (a IA
 * propõe, quem aplica é o dono). Parse tolerante: bloco malformado vira
 * texto normal com aviso discreto, nunca quebra a conversa.
 *
 * O texto do assistente renderiza como markdown SIMPLES (quebras de linha,
 * listas com "- " e **negrito**) — sem dependência pesada.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent as ClipboardEventReact,
  type ReactNode,
} from "react";
import {
  ActionIcon,
  Affix,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  Collapse,
  Drawer,
  FileButton,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconFile,
  IconMessageChatbot,
  IconPaperclip,
  IconSend,
  IconSparkles,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { api, ErroApi, urlComToken } from "./api";
import { fmtDataHora } from "./format";
import type { ArquivoCorrecoes } from "./correcoes-import";
import { Confirmacao, notificarAviso, notificarErroApi, notificarOk } from "./componentes/notificacoes";
import {
  extrairAcaoDoChat,
  montarArquivoCorrecoesDeAcao,
  normalizarMetaMensagem,
  type AnexoChatIa,
  type ChamadaFerramentaMeta,
  type MensagemChatIa,
  type PropostaJustificativa,
  type SubagenteMeta,
} from "./ia-tipos";

/** Limite do contrato do backend (POST chat, 04/09/2026): 5 anexos/mensagem. */
const MAX_ANEXOS = 5;
/** Tipos aceitos pelo POST .../ia/anexos (415 fora disso). */
const ACCEPT_ANEXOS = "image/png,image/jpeg,image/webp,image/gif,.txt,.csv,.md,.json,.log,application/pdf";

/** Nome automático de imagem colada do clipboard (print de tela): carimbo local. */
function nomePrintAgora(mime: string): string {
  const agora = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const ext = mime.split("/")[1] ?? "png";
  return `print-${agora.getFullYear()}${pad(agora.getMonth() + 1)}${pad(agora.getDate())}-${pad(agora.getHours())}${pad(agora.getMinutes())}${pad(agora.getSeconds())}.${ext}`;
}

/** Lê o arquivo como base64 (sem o prefixo "data:...;base64,") para o POST de anexos. */
function lerBase64(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      const resultado = String(leitor.result ?? "");
      resolve(resultado.slice(resultado.indexOf(",") + 1));
    };
    leitor.onerror = () => reject(new Error(`Não deu para ler o arquivo ${arquivo.name}.`));
    leitor.readAsDataURL(arquivo);
  });
}

/** Anexo em composição (upload antecipado): o chip mostra spinner até a API devolver o id. */
interface AnexoPendente {
  chave: string;
  nome: string;
  mime: string;
  tamanhoBytes: number;
  /** object URL local para thumbnail de imagem (revogada ao remover/limpar). */
  previewUrl: string | null;
  estado: "subindo" | "pronto";
  anexoId: string | null;
}

// ---------------------------------------------------------------------------
// Markdown simples (quebras de linha + listas "- " + **negrito**)
// ---------------------------------------------------------------------------

function renderizarNegrito(linha: string, keyBase: string): ReactNode[] {
  const partes = linha.split(/(\*\*[^*]+\*\*)/g);
  return partes.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={`${keyBase}-${i}`}>{p}</span>
    ),
  );
}

export function TextoMarkdownSimples({ texto }: { texto: string }) {
  const linhas = texto.split("\n");
  const blocos: ReactNode[] = [];
  let listaAtual: string[] = [];
  const fecharLista = (key: string) => {
    if (listaAtual.length === 0) return;
    blocos.push(
      <ul key={key} style={{ margin: "4px 0", paddingLeft: 18 }}>
        {listaAtual.map((item, i) => (
          <li key={i}>{renderizarNegrito(item, `${key}-${i}`)}</li>
        ))}
      </ul>,
    );
    listaAtual = [];
  };
  linhas.forEach((linha, i) => {
    const item = linha.match(/^\s*[-*]\s+(.*)$/);
    if (item) {
      listaAtual.push(item[1] ?? "");
      return;
    }
    fecharLista(`lista-${i}`);
    if (linha.trim() === "") {
      blocos.push(<div key={`sp-${i}`} style={{ height: 6 }} />);
    } else {
      blocos.push(<div key={`p-${i}`}>{renderizarNegrito(linha, `p-${i}`)}</div>);
    }
  });
  fecharLista("lista-fim");
  return (
    <Text size="sm" component="div" style={{ wordBreak: "break-word" }}>
      {blocos}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// Anexos de uma mensagem (usuário): thumbnail de imagem / link de arquivo —
// GET /ia/anexos/:id com token na query (padrão urlComToken para binário).
// ---------------------------------------------------------------------------

function AnexosDaMensagem({ anexos }: { anexos: AnexoChatIa[] }) {
  return (
    <Group gap={6} mt={4} wrap="wrap">
      {anexos.map((a) => {
        const href = urlComToken(`/ia/anexos/${a.id}`);
        if (a.mime.startsWith("image/")) {
          return (
            <a key={a.id} href={href} target="_blank" rel="noreferrer" title={`${a.nome} (abre em nova aba)`}>
              <img src={href} alt={a.nome} style={{ maxWidth: 96, maxHeight: 96, borderRadius: 6, display: "block" }} />
            </a>
          );
        }
        return (
          <Anchor key={a.id} href={href} target="_blank" size="xs" download={a.nome}>
            <Group gap={4} wrap="nowrap">
              <IconFile size={12} />
              <span>{a.nome}</span>
            </Group>
          </Anchor>
        );
      })}
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Trilha da resposta do assistente (mensagem.meta) — 04/09/2026
// ---------------------------------------------------------------------------

/** "Consultei: ler_txt_retorno ×2 · ler_espelho_folha" + detalhes colapsáveis. */
function TrilhaFerramentas({ ferramentas }: { ferramentas: ChamadaFerramentaMeta[] }) {
  const [aberto, setAberto] = useState(false);
  const contagem = new Map<string, number>();
  for (const f of ferramentas) contagem.set(f.nome, (contagem.get(f.nome) ?? 0) + 1);
  const agregado = [...contagem.entries()].map(([nome, n]) => (n > 1 ? `${nome} ×${n}` : nome)).join(" · ");
  return (
    <div>
      <Group gap={6} mt={4} wrap="nowrap">
        <Text size="xs" c="dimmed">
          Consultei: {agregado}
        </Text>
        <Anchor component="button" type="button" size="xs" onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
          {aberto ? "esconder detalhes" : "detalhes"}
        </Anchor>
      </Group>
      <Collapse expanded={aberto}>
        <Stack gap={4} mt={4}>
          {ferramentas.map((f, i) => (
            <div key={i}>
              <Text size="xs" ff="monospace">
                {f.nome}
                {f.argumentos != null ? ` ${JSON.stringify(f.argumentos)}` : ""}
              </Text>
              {f.resumo && (
                <Text size="xs" c="dimmed">
                  {f.resumo}
                </Text>
              )}
            </div>
          ))}
        </Stack>
      </Collapse>
    </div>
  );
}

/** Subagentes do modo profundo (objetivo + resumo de cada um). */
function TrilhaSubagentes({ subagentes }: { subagentes: SubagenteMeta[] }) {
  const [aberto, setAberto] = useState(false);
  return (
    <div>
      <Group gap={6} mt={4} wrap="nowrap">
        <Text size="xs" c="dimmed">
          Investigação profunda: {subagentes.length} subagente(s)
        </Text>
        <Anchor component="button" type="button" size="xs" onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
          {aberto ? "esconder detalhes" : "detalhes"}
        </Anchor>
      </Group>
      <Collapse expanded={aberto}>
        <Stack gap={4} mt={4}>
          {subagentes.map((s, i) => (
            <div key={i}>
              <Text size="xs" fw={600}>
                {s.objetivo}
              </Text>
              <Text size="xs" c="dimmed">
                {s.resumo}
              </Text>
            </div>
          ))}
        </Stack>
      </Collapse>
    </div>
  );
}

/** Proposta de justificativa da IA (ferramenta propor_justificativa): a IA
 *  NUNCA grava — o botão aplica com 1 clique humano via onAplicarJustificativa
 *  (POST /divergencias/:id/justificar da página, que já refaz o fetch). */
function CardProposta({
  proposta,
  onAplicarJustificativa,
}: {
  proposta: PropostaJustificativa;
  onAplicarJustificativa?: (divergenciaId: string, justificativa: string, referenciaEstudo?: string) => Promise<boolean>;
}) {
  const [estado, setEstado] = useState<"pendente" | "aplicando" | "aplicada">("pendente");
  return (
    <Paper withBorder p="xs" mt={6} data-testid="proposta-justificativa" style={{ borderColor: "var(--mantine-color-violet-4)" }}>
      <Group gap="xs" mb={4}>
        <IconSparkles size={14} color="var(--mantine-color-violet-7)" />
        <Text size="xs" fw={700} c="violet.8">
          Justificativa proposta pela IA
        </Text>
        <Code>{proposta.divergenciaId}</Code>
      </Group>
      <Text size="xs" mb={4} style={{ whiteSpace: "pre-wrap" }}>
        {proposta.justificativa}
      </Text>
      {proposta.referenciaEstudo && (
        <Text size="xs" c="dimmed" mb={4}>
          Referência: {proposta.referenciaEstudo}
        </Text>
      )}
      <Button
        size="xs"
        variant="light"
        color="violet"
        loading={estado === "aplicando"}
        disabled={estado === "aplicada" || !onAplicarJustificativa}
        title={
          onAplicarJustificativa
            ? "Grava a ciência da divergência com este texto — a IA propõe, a decisão é sua"
            : "Disponível na página da competência"
        }
        onClick={() => {
          if (!onAplicarJustificativa || estado !== "pendente") return;
          setEstado("aplicando");
          void onAplicarJustificativa(proposta.divergenciaId, proposta.justificativa, proposta.referenciaEstudo ?? undefined)
            .then((ok) => setEstado(ok ? "aplicada" : "pendente"))
            .catch(() => setEstado("pendente"));
        }}
      >
        {estado === "aplicada" ? "Aplicada ✓" : "Aplicar justificativa"}
      </Button>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Bolha de mensagem (com card de ação do bloco ```acao)
// ---------------------------------------------------------------------------

function BolhaMensagem({
  m,
  onPrepararCorrecao,
  onAplicarJustificativa,
}: {
  m: MensagemChatIa;
  onPrepararCorrecao?: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
  onAplicarJustificativa?: (divergenciaId: string, justificativa: string, referenciaEstudo?: string) => Promise<boolean>;
}) {
  const ehAssistente = m.papel === "assistant";
  const acaoInfo = ehAssistente ? extrairAcaoDoChat(m.conteudo) : null;
  const arquivo = acaoInfo?.acao ? montarArquivoCorrecoesDeAcao(acaoInfo.acao) : null;
  // 04/09/2026 — trilha do assistente (metaJson); ausente em mensagens antigas
  const meta = normalizarMetaMensagem(m.meta);
  return (
    <Paper
      withBorder
      p="xs"
      mb={6}
      bg={ehAssistente ? undefined : "sillis.0"}
      ml={ehAssistente ? 0 : "xl"}
      mr={ehAssistente ? "xl" : 0}
    >
      <Text size="xs" c="dimmed" mb={2}>
        {ehAssistente ? `IA · ${m.modelo ?? ""}` : "Você"} · {fmtDataHora(m.criadoEm)}
      </Text>
      <TextoMarkdownSimples texto={acaoInfo ? acaoInfo.textoSemBloco || "(ação proposta abaixo)" : m.conteudo} />
      {m.anexos && m.anexos.length > 0 && <AnexosDaMensagem anexos={m.anexos} />}
      {acaoInfo?.malformado && (
        <Text size="xs" c="yellow.8" mt={4}>
          ⚠ A IA tentou anexar uma ação, mas o formato veio inválido — o conteúdo acima é só texto, nada foi preparado.
        </Text>
      )}
      {ehAssistente && meta && (
        <>
          {meta.ferramentas && meta.ferramentas.length > 0 && <TrilhaFerramentas ferramentas={meta.ferramentas} />}
          {meta.subagentes && meta.subagentes.length > 0 && <TrilhaSubagentes subagentes={meta.subagentes} />}
          {meta.propostas?.map((p, i) => (
            <CardProposta key={`${p.divergenciaId}-${i}`} proposta={p} onAplicarJustificativa={onAplicarJustificativa} />
          ))}
          {meta.limiteAtingido && (
            <Alert color="yellow" variant="light" mt={6} p="xs">
              <Text size="xs">limite de investigação atingido — peça para continuar</Text>
            </Alert>
          )}
        </>
      )}
      {arquivo && onPrepararCorrecao && (
        <Paper withBorder p="xs" mt={6} bg="violet.0" data-testid="acao-chat-ia">
          <Group gap="xs" mb={4}>
            <IconSparkles size={14} color="var(--mantine-color-violet-7)" />
            <Text size="xs" fw={700} c="violet.8">
              Ação sugerida pela IA: <Code>{String(acaoInfo!.acao!.tipo)}</Code>
            </Text>
          </Group>
          <Button
            size="xs"
            variant="light"
            color="sillis"
            onClick={() => onPrepararCorrecao(arquivo, `sugestao-chat-ia-${String(acaoInfo!.acao!.tipo)}.json`)}
          >
            Preparar correção
          </Button>
          <Text size="xs" c="dimmed" mt={4}>
            Abre a prévia normal do import de correções — a IA propõe, quem decide aplicar é você.
          </Text>
        </Paper>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Botão flutuante + Drawer com o chat completo
// ---------------------------------------------------------------------------

export interface ChatIaFlutuanteProps {
  competenciaId: number;
  /** modelo escolhido no passo 7 (vai no body do POST; null = default da config). */
  modelo: string | null;
  onPrepararCorrecao?: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
  /** 04/09/2026 — aplica a justificativa proposta pela IA (clique humano);
   *  resolve true quando gravou (o botão do card vira "Aplicada ✓"). */
  onAplicarJustificativa?: (divergenciaId: string, justificativa: string, referenciaEstudo?: string) => Promise<boolean>;
}

export function ChatIaFlutuante({ competenciaId, modelo, onPrepararCorrecao, onAplicarJustificativa }: ChatIaFlutuanteProps) {
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState<MensagemChatIa[] | null>(null);
  const [erroCarga, setErroCarga] = useState<unknown>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  // 04/09/2026 — ajuda colapsável (FECHADA por padrão: o Alert fixo empurrava
  // o composer para fora da dobra) e modo profundo (mais rodadas/subagentes)
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [profundo, setProfundo] = useState(false);
  const [pendentes, setPendentes] = useState<AnexoPendente[]>([]);
  const [confirmandoLimpar, setConfirmandoLimpar] = useState(false);
  const [apagando, setApagando] = useState(false);
  const fimListaRef = useRef<HTMLDivElement | null>(null);
  const pendentesRef = useRef<AnexoPendente[]>([]);

  useEffect(() => {
    pendentesRef.current = pendentes;
  }, [pendentes]);

  // revoga as object URLs das thumbnails ao desmontar
  useEffect(
    () => () => {
      for (const p of pendentesRef.current) if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    },
    [],
  );

  /** Rótulo MM/AAAA da competência do chat (o histórico é POR COMPETÊNCIA). */
  function rotuloCompetencia(comp: number): string {
    return `${String(comp % 100).padStart(2, "0")}/${Math.floor(comp / 100)}`;
  }

  // 03/09/2026 (2ª leva, pedido do dono — "o histórico ficar vinculado por
  // competência"): o histórico SEMPRE foi gravado por competência no banco, mas
  // o painel carregava só na 1ª abertura — trocando de competência com a página
  // montada, a conversa ANTERIOR ficava na tela. Agora: trocou a competência,
  // zera o estado local (a recarga acontece na próxima abertura, pelo efeito
  // de carga abaixo).
  useEffect(() => {
    setMensagens(null);
    setErroCarga(null);
    setErroEnvio(null);
    setConfirmandoLimpar(false);
    limparPendentes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competenciaId]);

  /** Apaga a conversa desta competência (DELETE .../ia/chat — mensagens +
   *  anexos, com auditoria; as ANÁLISES de IA não são tocadas). */
  async function apagarConversa() {
    try {
      setApagando(true);
      const r = await api.del<{ mensagensApagadas: number; anexosApagados: number }>(`/competencias/${competenciaId}/ia/chat`);
      setMensagens([]);
      limparPendentes();
      setConfirmandoLimpar(false);
      notificarOk(`Conversa apagada (${r.mensagensApagadas} mensagens, ${r.anexosApagados} anexos).`);
    } catch (e) {
      notificarErroApi(e, "Não deu para apagar a conversa");
    } finally {
      setApagando(false);
    }
  }

  const carregar = useCallback(async () => {
    try {
      setErroCarga(null);
      const r = await api.get<{ total: number; mensagens: MensagemChatIa[] }>(`/competencias/${competenciaId}/ia/chat`);
      setMensagens(r.mensagens ?? []);
    } catch (e) {
      setErroCarga(e);
    }
  }, [competenciaId]);

  // carrega o histórico na PRIMEIRA abertura do painel (o Badge do botão
  // flutuante passa a mostrar o total depois disso)
  useEffect(() => {
    if (aberto && mensagens === null && erroCarga === null) void carregar();
  }, [aberto, mensagens, erroCarga, carregar]);

  const totalMensagens = mensagens?.length ?? 0;

  // auto-scroll ao fim: ao abrir o painel e a cada mensagem nova (04/09/2026)
  useEffect(() => {
    if (!aberto) return;
    const el = fimListaRef.current;
    // happy-dom (testes) não implementa scrollIntoView — guarda obrigatória
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "end" });
    }
  }, [aberto, totalMensagens]);

  function removerPendente(chave: string) {
    const alvo = pendentesRef.current.find((p) => p.chave === chave);
    if (alvo?.previewUrl) URL.revokeObjectURL(alvo.previewUrl);
    setPendentes((atual) => atual.filter((p) => p.chave !== chave));
  }

  function limparPendentes() {
    for (const p of pendentesRef.current) if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    pendentesRef.current = [];
    setPendentes([]);
  }

  /** Anexa arquivos (botão ou Ctrl+V) com upload ANTECIPADO: o chip mostra
   *  spinner até a API devolver o id; erro (413/415) = toast + chip removido. */
  function anexarArquivos(arquivos: { arquivo: File; nome: string }[]) {
    const livres = MAX_ANEXOS - pendentes.length;
    if (arquivos.length > livres) {
      notificarAviso(
        livres <= 0
          ? `Máximo de ${MAX_ANEXOS} anexos por mensagem — envie a mensagem antes de anexar mais.`
          : `Máximo de ${MAX_ANEXOS} anexos por mensagem — só ${livres} foi(ram) anexado(s).`,
      );
    }
    for (const { arquivo, nome } of arquivos.slice(0, Math.max(0, livres))) {
      const chave = `pend-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      let previewUrl: string | null = null;
      if (arquivo.type.startsWith("image/") && typeof URL.createObjectURL === "function") {
        try {
          previewUrl = URL.createObjectURL(arquivo);
        } catch {
          previewUrl = null;
        }
      }
      const mime = arquivo.type || "application/octet-stream";
      setPendentes((atual) => [...atual, { chave, nome, mime, tamanhoBytes: arquivo.size, previewUrl, estado: "subindo", anexoId: null }]);
      void (async () => {
        try {
          const conteudoBase64 = await lerBase64(arquivo);
          const r = await api.post<{ anexo: AnexoChatIa }>(`/competencias/${competenciaId}/ia/anexos`, {
            nome,
            mime,
            conteudoBase64,
          });
          setPendentes((atual) => atual.map((p) => (p.chave === chave ? { ...p, estado: "pronto", anexoId: r.anexo.id } : p)));
        } catch (e) {
          // 413/415 e afins: o anexo não entrou — toast + o chip some
          notificarErroApi(e, `Anexo não aceito: ${nome}`);
          removerPendente(chave);
        }
      })();
    }
  }

  /** Ctrl+V no campo: itens de arquivo do clipboard (print de tela = image/*)
   *  viram anexos pendentes automaticamente; texto colado segue normal. */
  function aoColar(e: ClipboardEventReact<HTMLTextAreaElement>) {
    const itens = e.clipboardData?.items;
    if (!itens) return;
    const arquivos: { arquivo: File; nome: string }[] = [];
    for (const item of Array.from(itens)) {
      if (item.kind !== "file") continue;
      const f = item.getAsFile();
      if (!f) continue;
      // print colado chega sem nome ou como "image.png" — nomear com carimbo
      const generico = !f.name || /^image\.(png|jpe?g|webp|gif)$/i.test(f.name);
      arquivos.push({ arquivo: f, nome: generico ? nomePrintAgora(f.type) : f.name });
    }
    if (arquivos.length === 0) return;
    e.preventDefault(); // arquivo colado não é texto — não sujar o campo
    anexarArquivos(arquivos);
  }

  async function enviar() {
    const conteudo = texto.trim();
    const prontos = pendentes.filter((p) => p.estado === "pronto" && p.anexoId !== null);
    if ((!conteudo && prontos.length === 0) || enviando) return;
    if (pendentes.some((p) => p.estado === "subindo")) {
      notificarAviso("Aguarde o upload dos anexos terminar antes de enviar.");
      return;
    }
    try {
      setEnviando(true);
      setErroEnvio(null);
      const r = await api.post<{ usuario: MensagemChatIa | null; assistente: MensagemChatIa | null }>(
        `/competencias/${competenciaId}/ia/chat`,
        {
          ...(conteudo ? { mensagem: conteudo } : {}),
          ...(modelo ? { modelo } : {}),
          ...(prontos.length > 0 ? { anexoIds: prontos.map((p) => p.anexoId!) } : {}),
          // "padrao" é o default do backend — só vai no body quando profundo
          ...(profundo ? { modo: "profundo" } : {}),
        },
      );
      setTexto("");
      limparPendentes();
      setMensagens((atual) => [
        ...(atual ?? []),
        ...(r.usuario ? [r.usuario] : []),
        ...(r.assistente ? [r.assistente] : []),
      ]);
    } catch (e) {
      // em erro da OpenRouter a mensagem do usuário JÁ está gravada no banco (e
      // os anexos vinculados a ela) — limpa o composer, recarrega o histórico
      // (sem duplicar a bolha) e mostra o erro como aviso
      if (e instanceof ErroApi && (e.corpo as { mensagemUsuarioGravada?: unknown } | null)?.mensagemUsuarioGravada) {
        setTexto("");
        limparPendentes();
        await carregar().catch(() => undefined);
      }
      const corpo = (e as ErroApi).corpo as { erro?: unknown } | null;
      setErroEnvio(
        e instanceof ErroApi && typeof corpo?.erro === "string" ? corpo.erro : "Não deu para conversar com a IA agora.",
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <Affix position={{ bottom: 24, right: 24 }}>
        <Tooltip label="Conversar com a IA sobre as divergências desta competência" position="left">
          <ActionIcon
            size={56}
            radius="xl"
            color="sillis"
            variant="filled"
            aria-label="Abrir chat com a IA"
            onClick={() => setAberto(true)}
          >
            <IconMessageChatbot size={28} />
          </ActionIcon>
        </Tooltip>
        {totalMensagens > 0 && !aberto && (
          <Badge
            color="red"
            variant="filled"
            size="sm"
            circle
            style={{ position: "absolute", top: -4, right: -4, pointerEvents: "none" }}
          >
            {totalMensagens}
          </Badge>
        )}
      </Affix>
      <Drawer
        opened={aberto}
        onClose={() => setAberto(false)}
        position="right"
        size={600}
        title={
          <Group gap="xs">
            <IconSparkles size={16} color="var(--mantine-color-sillis-7)" />
            <Text fw={700} size="sm">Chat com a IA — competência {rotuloCompetencia(competenciaId)}</Text>
          </Group>
        }
        styles={{
          // 04/09/2026 — coluna flex de altura total (fim do calc mágico da
          // ScrollArea): header no topo, body flex:1 com a lista rolável no
          // meio e o composer fixo no rodapé.
          content: { display: "flex", flexDirection: "column", overflow: "hidden" },
          body: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 },
        }}
      >
        {/* topo fixo: ajuda colapsável (fechada por padrão) + apagar + erro de carga */}
        <Box style={{ flex: "0 0 auto" }} mb="xs">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Anchor
              component="button"
              type="button"
              size="xs"
              c="dimmed"
              onClick={() => setAjudaAberta((v) => !v)}
              aria-expanded={ajudaAberta}
            >
              {ajudaAberta ? "esconder como funciona" : "como funciona?"}
            </Anchor>
            {totalMensagens > 0 && (
              <Tooltip label="Apaga TODA a conversa desta competência (mensagens e anexos) — as análises de IA do passo 6 não são tocadas" multiline w={280} position="left">
                <Anchor
                  component="button"
                  type="button"
                  size="xs"
                  c="red.7"
                  onClick={() => setConfirmandoLimpar(true)}
                  data-testid="apagar-conversa"
                >
                  <Group gap={4} wrap="nowrap">
                    <IconTrash size={12} />
                    <span>apagar conversa</span>
                  </Group>
                </Anchor>
              </Tooltip>
            )}
          </Group>
          <Collapse expanded={ajudaAberta}>
            <Alert color="sillis" variant="light" mt={4} data-testid="ajuda-chat">
              <Text size="xs">
                A IA responde com base nas divergências desta competência. Ela <strong>sugere</strong>, mas{" "}
                <strong>nunca aplica nada</strong> — quando propor uma correção, aparece o botão "Preparar correção" e a
                decisão final passa pela sua prévia.
                {modelo ? <> Modelo em uso nesta conversa: <Code>{modelo}</Code>.</> : null}
              </Text>
            </Alert>
          </Collapse>
          {erroCarga !== null && (
            <Alert color="red" title="Não deu para carregar a conversa" mt={4}>
              <Group gap="xs">
                <Text size="sm">Tente reabrir o painel.</Text>
                <Button size="xs" variant="light" onClick={() => { setErroCarga(null); void carregar(); }}>
                  Tentar de novo
                </Button>
              </Group>
            </Alert>
          )}
        </Box>
        {/* 03/09/2026 — confirmação do "apagar conversa" (pedido do dono) */}
        <Confirmacao
          aberta={confirmandoLimpar}
          titulo={`Apagar a conversa de ${rotuloCompetencia(competenciaId)}?`}
          consequencia={`Todas as ${totalMensagens} mensagens e os anexos desta competência serão apagados para sempre (as análises de IA do passo 6 não são tocadas). Deseja apagar mesmo?`}
          rotuloConfirmar={apagando ? "Apagando…" : "Apagar a conversa"}
          onConfirmar={() => void apagarConversa()}
          onCancelar={() => setConfirmandoLimpar(false)}
        />
        <ScrollArea style={{ flex: 1, minHeight: 0 }} offsetScrollbars data-testid="lista-chat">
          {mensagens !== null && mensagens.length === 0 && (
            <Text size="sm" c="dimmed" mb="xs">Nenhuma mensagem ainda — pergunte sobre uma divergência.</Text>
          )}
          {mensagens?.map((m) => (
            <BolhaMensagem key={m.id} m={m} onPrepararCorrecao={onPrepararCorrecao} onAplicarJustificativa={onAplicarJustificativa} />
          ))}
          {erroEnvio && (
            <Alert color="red" variant="light" mb="xs" withCloseButton onClose={() => setErroEnvio(null)} title="A IA não respondeu" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm">{erroEnvio} Sua mensagem foi registrada — tente enviar de novo.</Text>
            </Alert>
          )}
          <div ref={fimListaRef} />
        </ScrollArea>
        {/* rodapé fixo: anexos pendentes + modo profundo + composer */}
        <Box style={{ flex: "0 0 auto" }} pt="xs" data-testid="composer-chat">
          {pendentes.length > 0 && (
            <Group gap={6} mb={6} wrap="wrap">
              {pendentes.map((p) => (
                <Paper key={p.chave} withBorder p={4} radius="sm" data-testid="anexo-pendente">
                  <Group gap={6} wrap="nowrap">
                    {p.previewUrl ? (
                      <img src={p.previewUrl} alt={p.nome} style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }} />
                    ) : (
                      <IconFile size={16} />
                    )}
                    <Text size="xs" maw={180} lineClamp={1}>
                      {p.nome}
                    </Text>
                    {p.estado === "subindo" && <Loader size={12} />}
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="gray"
                      aria-label={`Remover anexo ${p.nome}`}
                      disabled={p.estado === "subindo"}
                      onClick={() => removerPendente(p.chave)}
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  </Group>
                </Paper>
              ))}
            </Group>
          )}
          <Group justify="space-between" align="center" gap="xs" mb={4} wrap="nowrap">
            <Tooltip label="Mais rodadas de ferramentas, subagentes e custo maior" multiline w={260}>
              <Switch
                size="xs"
                label="Investigação profunda"
                checked={profundo}
                onChange={(e) => setProfundo(e.currentTarget.checked)}
              />
            </Tooltip>
            {enviando && profundo && (
              <Text size="xs" c="dimmed">investigando… (análise profunda pode levar alguns minutos)</Text>
            )}
          </Group>
          <Group align="flex-end" wrap="nowrap" gap="xs">
            <FileButton
              multiple
              accept={ACCEPT_ANEXOS}
              onChange={(arquivos) => anexarArquivos(arquivos.map((a) => ({ arquivo: a, nome: a.name })))}
            >
              {(props) => (
                <Tooltip label="Anexar arquivos (imagem, texto ou PDF — até 5 por mensagem; Ctrl+V de print anexa direto)" multiline w={260} position="top">
                  <ActionIcon {...props} variant="light" color="sillis" size="lg" aria-label="Anexar arquivo">
                    <IconPaperclip size={18} />
                  </ActionIcon>
                </Tooltip>
              )}
            </FileButton>
            <Textarea
              style={{ flex: 1 }}
              autosize
              minRows={2}
              maxRows={6}
              placeholder="Pergunte sobre estas divergências… (Enter envia, Shift+Enter quebra linha)"
              value={texto}
              onChange={(e) => setTexto(e.currentTarget.value)}
              onPaste={aoColar}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void enviar();
                }
              }}
            />
            <Button
              onClick={() => void enviar()}
              loading={enviando}
              disabled={!texto.trim() && !pendentes.some((p) => p.estado === "pronto")}
              leftSection={<IconSend size={14} />}
            >
              Enviar
            </Button>
          </Group>
        </Box>
      </Drawer>
    </>
  );
}
